import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../session/session-context.js';
import { useTheme } from '../shell/theme.js';
import { AdminLogo } from '../shell/admin-logo.js';
import { Banner, Button, Card, CardBody, Field, Icon, Input } from '../ui/index.js';

const ERROR_LABEL: Record<string, string> = {
  username_and_password_required: 'Kullanıcı adı ve şifre zorunludur.',
  invalid_credentials: 'Kullanıcı adı veya şifre hatalı.',
  account_disabled: 'Hesabınız devre dışı bırakılmış. DB üzerinden admin_users.status alanını kontrol edin.',
  account_disabled_by_admin: 'Hesabınız bir yönetici tarafından devre dışı bırakılmış.',
  network_error: 'API sunucusuna ulaşılamadı.',
};

function describeError(reason: string | null): string {
  if (!reason) return '';
  return ERROR_LABEL[reason] ?? `Giriş başarısız (${reason}).`;
}

export function LoginPage(): JSX.Element {
  const session = useSession();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const from = search.get('from') ?? '/';
  const reason = search.get('reason');

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session.state.kind === 'cookie_session') {
    return <Navigate to={from} replace />;
  }

  if (session.state.kind === 'loading') {
    return (
      <LoginShellChrome theme={theme}>
        <Card>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 650 }}>Kopechka Yönetim</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Oturum doğrulanıyor…</p>
        </Card>
      </LoginShellChrome>
    );
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError('username_and_password_required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await session.signInWithPassword(username.trim(), password);
      if (result.kind === 'ok') {
        navigate(from, { replace: true });
        return;
      }
      if (result.kind === 'invalid_credentials') {
        setError('invalid_credentials');
      } else if (result.kind === 'account_disabled') {
        setError(result.reason);
      } else {
        setError(result.reason);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <LoginShellChrome theme={theme}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <span className="role-pill" style={{ marginBottom: 14 }}>
          <Icon.Lock size={11} />
          Operatör Konsolu
        </span>
        <h1
          style={{
            fontSize: 26,
            margin: '14px 0 6px',
            letterSpacing: '-0.012em',
            fontWeight: 650,
            color: 'var(--text-primary)',
          }}
        >
          Yönetim panelinde oturum aç
        </h1>
        <p className="t-muted" style={{ margin: 0, fontSize: 13.5 }}>
          Yetkilendirilmiş operatör hesabıyla giriş yapın. Tüm aksiyonlar audit'e yazılır.
        </p>
      </div>

      {reason === 'expired' ? (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="warn">Oturumun süresi doldu. Lütfen yeniden giriş yap.</Banner>
        </div>
      ) : reason === 'unauthenticated' ? (
        <div style={{ marginBottom: 14 }}>
          <Banner tone="warn">Bu sayfaya erişmek için giriş yapmalısın.</Banner>
        </div>
      ) : null}

      <Card flush>
        <CardBody>
          <form onSubmit={onSubmit} aria-label="yönetim girişi formu">
            <div style={{ marginBottom: 14 }}>
              <Field label="Operatör kullanıcı adı" htmlFor="username">
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  disabled={submitting}
                  placeholder="ör. mert.y"
                  required
                />
              </Field>
            </div>

            <div style={{ marginBottom: 18 }}>
              <Field label="Şifre" htmlFor="password">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={submitting}
                  placeholder="••••••••"
                  required
                  iconRight={
                    <button
                      type="button"
                      className="icon-right"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'şifreyi gizle' : 'şifreyi göster'}
                      tabIndex={-1}
                    >
                      {showPassword ? <Icon.EyeOff size={14} /> : <Icon.Eye size={14} />}
                    </button>
                  }
                />
              </Field>
            </div>

            <Button
              type="submit"
              variant="primary"
              block
              loading={submitting}
              iconRight={!submitting ? <Icon.ArrowUR size={14} /> : undefined}
            >
              {submitting ? 'Oturum açılıyor…' : 'Oturum aç'}
            </Button>

            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: 'var(--bg-sunken)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 8,
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
              }}
            >
              <Icon.Info size={14} style={{ color: 'var(--brand-600)', marginTop: 1, flexShrink: 0 }} />
              <div className="t-xs t-tertiary" style={{ lineHeight: 1.5 }}>
                Rol; sunucudaki <code style={{ fontFamily: 'var(--font-mono)' }}>admin_users.role</code> alanından
                gelir ve oturum kapasitelerini belirler. Bu form rolü değiştirmez — yalnızca kimlik doğrular.
              </div>
            </div>
          </form>

          {error ? (
            <div style={{ marginTop: 14 }}>
              <Banner tone="danger" testId="login-error">
                {describeError(error)}
              </Banner>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text-tertiary)' }}>
        Yardıma mı ihtiyacın var?{' '}
        <span style={{ color: 'var(--text-link)' }}>operator-help@kopechka</span>
      </div>
    </LoginShellChrome>
  );
}

function LoginShellChrome({
  theme,
  children,
}: {
  readonly theme: ReturnType<typeof useTheme>;
  readonly children: React.ReactNode;
}): JSX.Element {
  const themeIsDark = theme.theme === 'dark';
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page)',
      }}
    >
      <div
        style={{
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <AdminLogo />
        <button
          type="button"
          className="iconbtn"
          onClick={theme.toggleTheme}
          aria-label={themeIsDark ? 'açık temaya geç' : 'koyu temaya geç'}
          aria-pressed={themeIsDark}
          title={themeIsDark ? 'Açık temaya geç' : 'Koyu temaya geç'}
          data-testid="login-theme-toggle"
        >
          {themeIsDark ? <Icon.Sun /> : <Icon.Moon />}
        </button>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
        }}
      >
        <div style={{ width: 'min(440px, 100%)' }}>{children}</div>
      </div>
    </div>
  );
}
